import React, { useContext, useCallback, useMemo } from "react";
import "./M_LateralMenu.css";
import { NavigateContext, LoadingContext } from "src/M_Components";
import { useTranslation } from "react-i18next";

import {
  Box,
  ListItemText,
  ListItemButton,
  ListItem,
  IconButton,
  Tooltip,
} from "@mui/material";

import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUp from "@mui/icons-material/KeyboardArrowUp";
import TripOriginIcon from "@mui/icons-material/TripOrigin";
import ListItemIcon from "@mui/material/ListItemIcon";

import { DynamicMuiIcon } from "src/M_Components/M_Utils/DynamicMuiIcon";
import PropTypes from "prop-types";
import UserSettingsMenu from "src/components/settings/UserSettingsMenu";

/* =============================================================================
   MLateralMenu
   ---------------------------------------------------------------------------
   Purpose:
   - Renders the lateral navigation menu of the application shell.
   - Supports main menus, submenus, and third-level submenus.
   - Handles menu expand/collapse state through the external menus/setMenus pair.
   - Opens pages through NavigateContext helpers.
   - Applies theme colors received from LoadingContext.

   Defensive behavior:
   - The component normalizes "menus" into a safe array before any ".map(...)"
     usage so rendering does not crash when an invalid payload reaches the menu.
   - Submenu collections are also normalized before iteration.

   Layout notes:
   - Layout, spacing, animation, and responsive behavior are delegated to CSS.
   - This component focuses on menu rendering and interaction flow.
   ========================================================================== */
export function MLateralMenu({
  showMenu,
  setShowMenu,
  menus,
  setMenus,
  bodyPage,
  handleChangeLng,
  handleLogout,
  handleSettings,
  handleMenuPages,
}) {
  //region Contexts and derived values

  const ctx = useContext(NavigateContext);
  const ctx2 = useContext(LoadingContext);
  const { t } = useTranslation();

  // Resolve theme colors from LoadingContext with stable fallbacks.
  const secondaryColor = ctx2.secondaryColor || "var(--color-grey-dark)";
  const tertiaryColor = ctx2.tertiaryColor || "rgba(0, 0, 0, 0.04)";

  // Normalize the incoming menu payload once so every later access is safe.
  // This prevents runtime failures such as "menus.map is not a function"
  // when an invalid payload reaches the component during auth/session errors.
  const safeMenus = useMemo(() => {
    return Array.isArray(menus) ? menus : [];
  }, [menus]);

  //endregion

  //region Private collection helpers

  /**
   * Returns a safe submenu array for a given menu node.
   *
   * Purpose:
   * - Centralizes the normalization of SubMenus collections.
   * - Prevents direct ".map(...)" or ".length" usage over non-array values.
   *
   * @param {object} menuNode - Menu node that may contain a SubMenus collection.
   * @returns {Array} Safe submenu array.
   */
  const getSafeSubMenus = useCallback((menuNode) => {
    return Array.isArray(menuNode?.SubMenus) ? menuNode.SubMenus : [];
  }, []);

  /**
   * Determines whether a menu node contains at least one submenu entry.
   *
   * @param {object} menuNode - Menu node to evaluate.
   * @returns {boolean} True when the node contains child submenu entries.
   */
  const hasSubMenus = useCallback(
    (menuNode) => getSafeSubMenus(menuNode).length > 0,
    [getSafeSubMenus]
  );

  //endregion

  //region Menu state helpers

  /**
   * Toggles the open state of one submenu inside a specific main menu.
   *
   * Purpose:
   * - Keeps only the targeted submenu open.
   * - Closes sibling submenus under the same main menu.
   * - Leaves other main menus unchanged.
   *
   * Defensive behavior:
   * - Uses normalized menu collections to prevent invalid ".map(...)" calls.
   *
   * @param {object} menu - Parent main menu that owns the submenu collection.
   * @param {object} subMenu - Submenu node whose open state should be toggled.
   */
  const updateMenu = useCallback(
    (menu, subMenu) => {
      const nextMenus = safeMenus.map((elem) => {
        if (elem.MenuLabel === menu.MenuLabel) {
          const safeChildMenus = getSafeSubMenus(elem);

          return {
            ...elem,
            SubMenus: safeChildMenus.map((elem2) => ({
              ...elem2,
              MenuOpen:
                elem2.MenuLabel === subMenu?.MenuLabel
                  ? !elem2.MenuOpen
                  : false,
            })),
          };
        }

        return elem;
      });

      setMenus(nextMenus);
    },
    [safeMenus, getSafeSubMenus, setMenus]
  );

  /**
   * Toggles the open state of a main menu and closes every other main menu.
   *
   * Purpose:
   * - Maintains a single expanded main menu at a time.
   * - Resets all second-level submenu open states when a main menu is opened.
   *
   * Defensive behavior:
   * - Uses normalized menu collections to prevent invalid ".map(...)" calls.
   *
   * @param {object} menu - Main menu whose open state should be toggled.
   */
  const changeMenu = useCallback(
    (menu) => {
      const nextMenus = safeMenus.map((elem) => {
        if (menu?.MenuLabel === elem.MenuLabel) {
          const menuOpen = !elem.MenuOpen;
          const safeChildMenus = getSafeSubMenus(elem);

          return {
            ...elem,
            MenuOpen: menuOpen,
            SubMenus: menuOpen
              ? safeChildMenus.map((sub) => ({ ...sub, MenuOpen: false }))
              : safeChildMenus,
          };
        }

        return { ...elem, MenuOpen: false };
      });

      setMenus(nextMenus);
    },
    [safeMenus, getSafeSubMenus, setMenus]
  );

  //endregion

  //region Click handlers

  /**
   * Handles click interaction over a main menu entry.
   *
   * Behavior:
   * - When the menu has no submenus, closes the lateral menu and opens the page.
   * - When the menu has submenus, toggles its expanded state.
   *
   * @param {React.MouseEvent} event - Click event.
   * @param {object} menu - Clicked main menu node.
   */
  const onClickMenu = (event, menu) => {
    if (!hasSubMenus(menu)) {
      setShowMenu(false);
      handleMenuPages(menu.MenuLabelUrl, t(menu.MenuDisplay), event);
      return;
    }

    changeMenu(menu);
  };

  /**
   * Handles click interaction over a second-level submenu entry.
   *
   * Behavior:
   * - When the submenu has no children, closes the lateral menu and opens the page.
   * - When the submenu has children, toggles only that submenu under its parent menu.
   *
   * @param {React.MouseEvent} event - Click event.
   * @param {object} menu - Parent main menu.
   * @param {object} subMenu - Clicked submenu node.
   */
  const onClickSubMenu = (event, menu, subMenu) => {
    if (!hasSubMenus(subMenu)) {
      setShowMenu(false);
      handleMenuPages(subMenu?.MenuLabelUrl, t(subMenu?.MenuDisplay), event);
      return;
    }

    const nextMenus = safeMenus.map((elem) => {
      if (elem.MenuLabel === menu.MenuLabel) {
        const safeChildMenus = getSafeSubMenus(elem);

        return {
          ...elem,
          SubMenus: safeChildMenus.map((elem2) => ({
            ...elem2,
            MenuOpen:
              elem2.MenuLabel === subMenu.MenuLabel ? !elem2.MenuOpen : false,
          })),
        };
      }

      return elem;
    });

    setMenus(nextMenus);
  };

  //endregion

  //region Item render helpers

  /**
   * Renders a second-level submenu entry.
   *
   * Purpose:
   * - Keeps submenu rendering isolated and readable.
   * - Supports left click and middle click behavior.
   *
   * Interaction behavior:
   * - Left click follows the standard submenu flow.
   * - Middle click opens the destination in a new tab when the submenu has no
   *   nested children.
   * - Middle click toggles submenu expansion when the submenu owns child items.
   *
   * @param {object} menu - Parent main menu.
   * @param {object} subMenu - Submenu node to render.
   * @returns {JSX.Element} Rendered submenu item.
   */
  const subMenuListItem = (menu, subMenu) => (
    <ListItemButton
      disableRipple
      key={subMenu?.MenuLabel}
      className={`menuSubItem ${subMenu?.MenuOpen ? "open" : ""}`}
      onClick={(event) => onClickSubMenu(event, menu, subMenu)}
      onMouseDown={(event) => {
        if (event.button === 1) {
          if (!hasSubMenus(subMenu)) {
            document.body.onmousedown = (e) => {
              if (e.button === 1) return false;
            };

            ctx.newTab(
              `${subMenu?.MenuLabelUrl}`,
              `${t(subMenu?.MenuDisplay)}`,
              event
            );

            return;
          }

          updateMenu(menu, subMenu);
        }
      }}
    >
      <ListItemText
        primary={
          <span className="menuLabelContainer">
            <span className="menuLabelText">{t(subMenu?.MenuDisplay)}</span>
          </span>
        }
      />
    </ListItemButton>
  );

  /**
   * Renders a third-level submenu entry.
   *
   * Purpose:
   * - Keeps third-level rendering isolated and readable.
   * - Supports left click and middle click to open the target route in a new tab.
   *
   * @param {object} subSubMenu - Third-level submenu node.
   * @returns {JSX.Element} Rendered third-level submenu item.
   */
  const subSubMenuListItem = (subSubMenu) => (
    <ListItemButton
      key={subSubMenu?.MenuLabel}
      className="menuSubSubItem"
      onClick={(event) => {
        setShowMenu(false);
        ctx.newTab(
          `${subSubMenu.MenuLabelUrl}`,
          `${t(subSubMenu.MenuDisplay)}`,
          event
        );
      }}
      onMouseDown={(event) => {
        if (event.button === 1) {
          document.body.onmousedown = (e) => {
            if (e.button === 1) return false;
          };

          ctx.newTab(
            `${subSubMenu.MenuLabelUrl}`,
            `${t(subSubMenu.MenuDisplay)}`,
            event
          );
        }
      }}
    >
      <ListItemIcon className="subSubMenuIcon">
        <TripOriginIcon />
      </ListItemIcon>

      <ListItemText
        primary={
          <span className="menuLabelContainer">
            <span className="menuLabelText">
              {t(subSubMenu.MenuLabel)}
            </span>
          </span>
        }
      />
    </ListItemButton>
  );

  //endregion

  //region Render

  return (
    <div className="homepageTemplate">
      <div
        className={`homePageMenuTemplate ${showMenu ? "open" : "closed"}`}
        style={{
          "--menu-secondary": secondaryColor,
          "--menu-tertiary": tertiaryColor,
        }}
      >
        <div className="principalMenu">
          <div className="menuScrollArea">
            <div className="principalMenuTitle">
              <span className="principalMenuTitleText">{t("Menu")}</span>
            </div>

            {safeMenus.map((menu, key) => {
              const menuSubMenus = getSafeSubMenus(menu);

              return (
                <div className="menuBlock" key={menu?.MenuLabel || key}>
                  <Box
                    className={`menuItemWrapper ${menu.MenuOpen ? "open" : ""}`}
                  >
                    <Tooltip title={t(menu.MenuDisplay)} placement="right">
                      <ListItem component="div" disablePadding>
                        <ListItemButton
                          disableRipple
                          className="mainMenuItem"
                          onClick={(event) => onClickMenu(event, menu)}
                          onMouseDown={(event) => {
                            if (event.button === 1) {
                              document.body.onmousedown = (e) => {
                                if (e.button === 1) return false;
                              };

                              if (!hasSubMenus(menu)) {
                                ctx.newTab(
                                  `${menu?.MenuLabelUrl}`,
                                  `${t(menu?.MenuDisplay)}`,
                                  event
                                );
                                return;
                              }

                              changeMenu(menu);
                            }
                          }}
                        >
                          {/* Icon state is styled through CSS classes and the
                              original filter behavior is preserved here. */}
                          <DynamicMuiIcon
                            iconName={menu.MenuIcon}
                            className={`menuIcon ${menu.MenuOpen ? "menuIcon-open" : ""
                              }`}
                            sx={{
                              filter: menu.MenuOpen
                                ? menu.MenuIconFilter
                                : "invert(35%) sepia(0%) saturate(34%) hue-rotate(170deg) brightness(84%) contrast(91%)",
                            }}
                          />

                          <ListItemText
                            primary={
                              <span className="menuLabelContainer">
                                <span className="menuLabelText">
                                  {t(menu?.MenuDisplay)}
                                </span>
                              </span>
                            }
                          />

                          {menuSubMenus.length > 0 && (
                            <IconButton size="large" disableRipple>
                              {menu?.MenuOpen ? (
                                <KeyboardArrowUp
                                  style={{ color: secondaryColor }}
                                />
                              ) : (
                                <KeyboardArrowDown
                                  style={{ color: "var(--color-grey-dark)" }}
                                />
                              )}
                            </IconButton>
                          )}
                        </ListItemButton>
                      </ListItem>
                    </Tooltip>

                    {menu?.MenuOpen &&
                      menuSubMenus.map((subMenu, subMenuKey) => {
                        const subSubMenus = getSafeSubMenus(subMenu);

                        return (
                          <div key={subMenu?.MenuLabel || subMenuKey}>
                            <Tooltip
                              title={t(subMenu?.MenuDisplay)}
                              placement="right"
                            >
                              {subMenuListItem(menu, subMenu)}
                            </Tooltip>

                            {subMenu?.MenuOpen &&
                              subSubMenus.map((subSubMenu, subSubMenuKey) => (
                                <div
                                  key={subSubMenu?.MenuLabel || subSubMenuKey}
                                >
                                  <Tooltip
                                    title={t(subSubMenu.MenuLabel)}
                                    placement="right"
                                  >
                                    {subSubMenuListItem(subSubMenu)}
                                  </Tooltip>
                                </div>
                              ))}
                          </div>
                        );
                      })}
                  </Box>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom action area for settings, language, and logout controls. */}
        <div className="userSettingsMenu">
          <UserSettingsMenu
            handleSettings={handleSettings}
            handleLogout={handleLogout}
            handleChangeLng={handleChangeLng}
          />
        </div>
      </div>

      {/* Main page content rendered beside the lateral menu. */}
      {bodyPage}
    </div>
  );

  //endregion
}

MLateralMenu.propTypes = {
  showMenu: PropTypes.bool,
  setShowMenu: PropTypes.func,
  menus: PropTypes.array,
  setMenus: PropTypes.func,
  bodyPage: PropTypes.node,
  handleChangeLng: PropTypes.func,
  handleLogout: PropTypes.func,
  handleSettings: PropTypes.func,
  handleMenuPages: PropTypes.func,
};